import { useEffect, useRef } from 'react';

const HEARTBEAT_INTERVAL_S = 15;
const MAX_SECONDS_PER_REQUEST = 3600;

interface UseTimeSpentTrackerOptions {
  apiBaseUrl: string;
  companyId: string;
  token: string;
  enabled: boolean;
}

/**
 * Buffers active viewing seconds and POSTs them to
 * /public/quote-shares/{companyId}/{token}/time-spent — heartbeat every 15s
 * plus a sendBeacon flush on unload. Pauses while the tab is hidden so an
 * abandoned tab doesn't pad the counter.
 *
 * The buffer is only deducted after a successful 2xx response — failed
 * requests are retried on the next heartbeat without double-counting. A 410
 * stops the tracker entirely (share revoked or expired mid-view).
 */
export const useTimeSpentTracker = ({
  apiBaseUrl,
  companyId,
  token,
  enabled
}: UseTimeSpentTrackerOptions) => {
  // Refs so the running intervals always see the latest values without
  // resubscribing.
  const elapsedBufferRef = useRef(0);
  const lastTickAtRef = useRef(Date.now());
  const isActiveRef = useRef(
    typeof document !== 'undefined' ? !document.hidden : true
  );
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !apiBaseUrl || !companyId || !token) return undefined;

    const baseUrl = `${apiBaseUrl}/public/quote-shares/${companyId}/${token}/time-spent`;

    elapsedBufferRef.current = 0;
    lastTickAtRef.current = Date.now();
    isActiveRef.current = !document.hidden;
    stoppedRef.current = false;

    const accrue = () => {
      if (stoppedRef.current) return;
      const now = Date.now();
      if (isActiveRef.current) {
        const delta = Math.floor((now - lastTickAtRef.current) / 1000);
        if (delta > 0) {
          elapsedBufferRef.current += delta;
          lastTickAtRef.current += delta * 1000;
        }
      } else {
        lastTickAtRef.current = now;
      }
    };

    const flush = async () => {
      if (stoppedRef.current) return;
      accrue();
      const seconds = Math.min(
        elapsedBufferRef.current,
        MAX_SECONDS_PER_REQUEST
      );
      if (seconds <= 0) return;

      elapsedBufferRef.current -= seconds;
      try {
        const res = await fetch(baseUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ seconds }),
          keepalive: true
        });
        if (res.status === 410) {
          stoppedRef.current = true;
          return;
        }
        if (!res.ok) throw new Error(`status_${res.status}`);
      } catch {
        // Network failure or non-OK: put the seconds back so the next
        // heartbeat retries. Never double-count, never block the UI.
        elapsedBufferRef.current += seconds;
      }
    };

    const flushBeacon = () => {
      if (stoppedRef.current) return;
      accrue();
      const seconds = Math.min(
        elapsedBufferRef.current,
        MAX_SECONDS_PER_REQUEST
      );
      if (seconds <= 0) return;
      elapsedBufferRef.current = 0;
      try {
        const blob = new Blob([JSON.stringify({ seconds })], {
          type: 'application/json'
        });
        navigator.sendBeacon?.(baseUrl, blob);
      } catch {
        // sendBeacon may throw if the page is fully torn down; nothing we can
        // do at that point.
      }
    };

    const tickHandle = window.setInterval(accrue, 1000);
    const heartbeatHandle = window.setInterval(
      flush,
      HEARTBEAT_INTERVAL_S * 1000
    );

    const handleVisibilityChange = () => {
      // Drain whatever active time we had since the last tick *before*
      // toggling, so we don't lose the tail or carry stale time forward.
      accrue();
      isActiveRef.current = !document.hidden;
      lastTickAtRef.current = Date.now();
      if (document.hidden) flush();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', flushBeacon);
    window.addEventListener('beforeunload', flushBeacon);

    return () => {
      window.clearInterval(tickHandle);
      window.clearInterval(heartbeatHandle);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', flushBeacon);
      window.removeEventListener('beforeunload', flushBeacon);
      // Best-effort flush of whatever is buffered when the component unmounts
      // (e.g. share status flips to revoked, route change).
      flushBeacon();
    };
  }, [apiBaseUrl, companyId, token, enabled]);
};
